import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { isStaffRole } from '@wanderlust/shared';
import { useAuth } from '../hooks/useAuth';
import { canSeeAnder } from '../lib/anderAccess';
import { Button, Icon, IconButton } from './ui';

export interface SiteHeaderProps {
  /**
   * Lift the header out of the flow and drop its navy, for a page that runs
   * its own artwork up behind it (the marketing hero's photograph). The page
   * underneath owes the header its height back as top padding.
   */
  overlay?: boolean;
}

/** The width the inline link row appears at, matching Tailwind's `md`. */
const DESKTOP_QUERY = '(min-width: 768px)';

/**
 * The site's one header: navy band, horizontal lockup at the left, links and
 * the planning CTA at the right. Every public page hangs it above its own
 * content, so the logo lives here rather than inside any page's hero — that's
 * what lets the marketing hero be a plain text block instead of a two-column
 * layout that has to re-tune the logo at four breakpoints.
 *
 * One 1200px rail on every page that mounts it, matching the marketing
 * page's sections, so the lockup holds its left edge as the reader crosses
 * from the marketing page into the blog and back.
 *
 * Below md the links collapse into a sheet: the widest signed-in case (About,
 * Blog, Admin, My Trips, Logout, plus the CTA) is more chrome than can share
 * a row with the lockup on a phone.
 *
 * The open sheet is the whole viewport, not a strip under the header. On the
 * marketing page the header is `absolute` over the hero photograph, so a
 * content-height panel left the headline showing underneath it and the links
 * sat on whatever the picture happened to be doing. `max-md:fixed inset-0`
 * makes the header itself the sheet while it is open, which also means the
 * lockup and the close button ride along at the top instead of being covered
 * by it. The `max-md:` prefix is what puts everything back at desktop width
 * without a resize handler having to unwind it.
 */
export function SiteHeader({ overlay = false }: SiteHeaderProps) {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const toggleRef = useRef<HTMLButtonElement>(null);

  const handleLogout = async () => {
    await signOut();
    navigate('/signin');
  };

  /**
   * Close because the reader is leaving: a link or the CTA was followed. The
   * page they land on owns the focus from here, so this must not claw it back.
   */
  const dismissMenu = useCallback(() => setMenuOpen(false), []);

  /**
   * Close because they asked to (the X, or Escape) and are staying put. Focus
   * goes back to the toggle so a keyboard doesn't restart at the top of the
   * document.
   */
  const closeMenu = useCallback(() => {
    setMenuOpen(false);
    toggleRef.current?.focus();
  }, []);

  // Everything the open sheet owns outside its own markup: the page behind it
  // must not scroll under a finger, Escape must close it, and a viewport that
  // grows past md must not leave a phone menu open over a desktop header (the
  // toggle that closes it is display:none up there).
  useEffect(() => {
    if (!menuOpen) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeMenu();
    };
    document.addEventListener('keydown', onKeyDown);

    // Guarded because this is not universal: jsdom has no matchMedia, and
    // neither do some embedded webviews. Losing the breakpoint watcher costs
    // one edge case; throwing here would cost the whole menu.
    const desktop =
      typeof window.matchMedia === 'function' ? window.matchMedia(DESKTOP_QUERY) : null;
    // No focus return here: at desktop width the toggle is hidden, and
    // focusing a hidden element is how you lose the caret entirely.
    const onBreakpoint = (event: MediaQueryListEvent) => {
      if (event.matches) setMenuOpen(false);
    };
    desktop?.addEventListener('change', onBreakpoint);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', onKeyDown);
      desktop?.removeEventListener('change', onBreakpoint);
    };
  }, [menuOpen, closeMenu]);

  const rail = 'max-w-[1200px] mx-auto px-6';
  // Over a photograph the secondary links need more weight than they do on
  // flat navy: white/60 drops to ~4.2:1 against the brightest thing the
  // marketing hero's picture puts behind them, which is under AA. Inside the
  // open sheet the background is flat navy again whatever the page is, so the
  // links there set their own tone rather than inheriting the overlay's.
  const linkTone = overlay ? 'text-white/75' : 'text-white/60';
  const linkClass = `${linkTone} hover:text-white focus-visible:text-white transition-colors`;
  const sheetLinkClass = 'text-white/80 hover:text-white focus-visible:text-white transition-colors';

  /**
   * Rendered inline at md+ and inside the sheet below it. Only ever one copy in
   * the DOM: the sheet is unmounted while closed, and the inline row is only
   * hidden by CSS when it isn't.
   */
  const navItems = (itemClass: string, tone: string, onNavigate?: () => void) => (
    <>
      {/* The introduction on the home page. A Link with a fragment rather than
          a plain anchor, so following it from the blog doesn't reload the app;
          ScrollToTop is what lands the page on the section. */}
      <Link to="/#about" className={`${tone} ${itemClass}`} onClick={onNavigate}>
        About
      </Link>
      <Link to="/blog" className={`${tone} ${itemClass}`} onClick={onNavigate}>
        Blog
      </Link>
      {canSeeAnder(user?.role) && (
        <Link to="/ander" className={`${tone} ${itemClass}`} onClick={onNavigate}>
          Ask Ander
        </Link>
      )}
      {isStaffRole(user?.role) && (
        <Link to="/admin" className={`${tone} ${itemClass}`} onClick={onNavigate}>
          Admin
        </Link>
      )}
      {user ? (
        <>
          <Link to="/trips" className={`${tone} ${itemClass}`} onClick={onNavigate}>
            My Trips
          </Link>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              onNavigate?.();
              handleLogout();
            }}
            className={`px-0 py-0 rounded-none font-normal ${tone} hover:bg-transparent hover:text-white ${itemClass}`}
          >
            Logout
          </Button>
        </>
      ) : (
        <Link
          to="/signin"
          className={`${tone} ${itemClass}`}
          data-guest-only
          onClick={onNavigate}
        >
          Traveler Login
        </Link>
      )}
    </>
  );

  const base = overlay ? 'absolute inset-x-0 top-0 z-20' : 'bg-primary';
  // Opaque, the full viewport, and scrollable in its own right, so a short
  // phone in landscape can still reach the CTA at the bottom of the list.
  const openSheet = 'max-md:fixed max-md:inset-0 max-md:z-50 max-md:bg-primary max-md:overflow-y-auto';

  return (
    <header className={`text-white ${base} ${menuOpen ? openSheet : ''}`}>
      <nav aria-label="Main">
        <div className={`${rail} py-4 flex items-center justify-between gap-4`}>
          {/*
            The lockup is the only thing in the header with a size worth
            arguing about, so it gets the room: 96px at sm+, a 350px-wide mark
            that carries the band on its own. Phones take 64, which is what
            the row has to spare now that only the menu button shares it.

            min-w-0 + max-w-full + object-contain then does the work no
            breakpoint should have to. The link is the only shrinkable item in
            the row (the CTA is nowrap), so a tight width shrinks its box and
            object-contain scales the artwork down inside it — 59px at 320,
            and below the full 96 for a signed-in admin at 768, where the full
            link row is widest — instead of pushing anything off the edge.
          */}
          <Link to="/" className="min-w-0 hover:opacity-80 transition-opacity" onClick={dismissMenu}>
            <img
              src="/logo-horizontal.svg"
              alt="Wanderlust Cravings"
              width="182"
              height="50"
              className="h-16 sm:h-24 w-auto max-w-full object-contain"
            />
          </Link>

          <div className="flex items-center gap-4 sm:gap-6">
            <div className="hidden md:flex items-center gap-6">{navItems('', linkClass)}</div>
            {/* Desktop only. The sheet carries its own copy below, where it can
                be the full-width primary action instead of a nowrap pill. */}
            <Link
              to="/plan"
              className="hidden md:inline-block px-5 py-2.5 bg-accent hover:bg-accent-hover text-primary font-medium rounded transition-colors whitespace-nowrap"
            >
              Plan a Trip
            </Link>
            <IconButton
              ref={toggleRef}
              onClick={() => (menuOpen ? closeMenu() : setMenuOpen(true))}
              aria-expanded={menuOpen}
              aria-controls="site-menu"
              aria-label={menuOpen ? 'Close menu' : 'Open menu'}
              className="md:hidden -mr-2 p-2 text-white hover:bg-white/10 cursor-pointer"
            >
              <Icon name={menuOpen ? 'X' : 'Menu'} size="lg" color="inverse" />
            </IconButton>
          </div>
        </div>

        {menuOpen && (
          <div id="site-menu" className="md:hidden">
            <div className={`${rail} pb-10 flex flex-col items-stretch`}>
              <div className="flex flex-col items-start">
                {navItems('py-3 text-lg', sheetLinkClass, dismissMenu)}
              </div>
              {/* The reason the menu is open, at the bottom where a thumb is:
                  every other item here is somewhere to read, and this is the
                  one that starts a trip. */}
              <Link
                to="/plan"
                onClick={dismissMenu}
                className="mt-6 rounded bg-accent px-5 py-3.5 text-center font-medium text-primary transition-colors hover:bg-accent-hover"
              >
                Plan a Trip
              </Link>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
}
